import type { ConversationPanelState } from "@circle-react/components/shared/uikit/CircleAIAppShell/types";

import { buildEntryUrl } from "@/react/helpers/entryUrlHelpers";

// The global CircleAI URL contract. The intent, conversation identifier, and
// presentation work on any route, are consumed once, and are stripped
// afterwards.
//
//   ?ai=new&view_type=full_screen                         blank, full width
//   ?ai=panel                                              active conversation, docked
//   ?ai_conversation_id=<uuid>&view_type=panel            conversation, docked
//
// Intent and identifier stay separate instead of overloading `ai` with both,
// matching the split already established by `handoff.ts` between
// `circle_ai_panel` (command) and `circle_ai_conversation` (id).
export const AI_PARAM = "ai";
export const AI_CONVERSATION_ID_PARAM = "ai_conversation_id";
export const VIEW_TYPE_PARAM = "view_type";

export const AI_INTENT_NEW = "new";
const AI_INTENT_PANEL = "panel";

export const AI_VIEW_FULLSCREEN = "fullscreen";
export const AI_VIEW_DEFAULT = "default";
export const VIEW_TYPE_FULL_SCREEN = "full_screen";
const VIEW_TYPE_PANEL = "panel";

export type AiIntent = typeof AI_INTENT_NEW | typeof AI_INTENT_PANEL;
export type AiView = Exclude<ConversationPanelState, "closed">;

export interface CircleAiUrlEntry {
  conversationId: string | null;
  intent: AiIntent | null;
  view: AiView;
}

export interface CircleAiSearchParams {
  [key: string]: unknown;
  [AI_CONVERSATION_ID_PARAM]?: string;
  [AI_PARAM]?: string;
  [VIEW_TYPE_PARAM]?: string;
}

export const stripAiParams = (
  search: CircleAiSearchParams,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(search).filter(
      ([key]) =>
        key !== AI_PARAM &&
        key !== AI_CONVERSATION_ID_PARAM &&
        key !== VIEW_TYPE_PARAM,
    ),
  );

const isAiIntent = (value: string | undefined): value is AiIntent =>
  value === AI_INTENT_NEW || value === AI_INTENT_PANEL;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Returns `null` when the URL asks for nothing, so callers can bail before
 * touching any store.
 *
 * A legacy `?ai=<uuid>` (minted by the Stripe return URL in
 * `create_checkout_session_service.rb`) is normalised to a conversation id, but
 * only when it actually looks like one — so `?ai=New` is inert, and an intent
 * value added later is not mistaken for an id by a client that predates it.
 */
export const parseCircleAiUrlEntry = (
  search: CircleAiSearchParams,
): CircleAiUrlEntry | null => {
  const rawIntent = search[AI_PARAM];
  const intent = isAiIntent(rawIntent) ? rawIntent : null;
  const legacyConversationId =
    !intent && rawIntent && UUID_PATTERN.test(rawIntent) ? rawIntent : null;
  const conversationId =
    search[AI_CONVERSATION_ID_PARAM] ?? legacyConversationId;

  if (!intent && !conversationId) return null;

  const view =
    search[VIEW_TYPE_PARAM] === VIEW_TYPE_FULL_SCREEN
      ? AI_VIEW_FULLSCREEN
      : AI_VIEW_DEFAULT;

  return { conversationId: conversationId ?? null, intent, view };
};

/**
 * Absolute URL that opens CircleAI, at `view`, on the page the user is
 * currently on — the rest of that page's query string is carried along, so
 * copying from e.g. `/settings/workflows?tab=active` keeps the tab.
 *
 * A `conversationId` reopens that conversation; `null` starts a blank one.
 */
export const buildCircleAiEntryUrl = (
  conversationId: string | null,
  view: AiView = AI_VIEW_DEFAULT,
  location: Location = window.location,
): string =>
  // Overwrite all three keys rather than merging: a stale one left on the
  // current URL must not ride along and contradict the link being built.
  // The presentation stays explicit even for the docked panel, which makes the
  // generated URL self-contained if the default ever changes.
  buildEntryUrl(
    {
      [AI_PARAM]: conversationId ? null : AI_INTENT_NEW,
      [AI_CONVERSATION_ID_PARAM]: conversationId || null,
      [VIEW_TYPE_PARAM]:
        view === AI_VIEW_FULLSCREEN ? VIEW_TYPE_FULL_SCREEN : VIEW_TYPE_PANEL,
    },
    location,
  );
